import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge as UiBadge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, BookOpen, Trophy, User, LogOut,
  ChevronLeft, Lock, Award, Crown, Flame, Star,
  BarChart3, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Achievements() {
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [allBadges, setAllBadges] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [earnedDetails, setEarnedDetails] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allRes, userRes, leaderRes] = await Promise.all([
          axios.get(`${API}/badges`, { withCredentials: true }),
          axios.get(`${API}/badges/user`, { withCredentials: true }),
          axios.get(`${API}/leaderboard`, { withCredentials: true }).catch(() => ({ data: { leaderboard: [] } }))
        ]);
        setAllBadges(allRes.data);
        setEarnedBadges(userRes.data.earned || []);
        setEarnedDetails(userRes.data.earned_details || []);
        setLeaderboard(leaderRes.data.leaderboard || []);
      } catch (error) {
        toast.error('Failed to load achievements');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getBadgeName = (badge) => {
    if (language === 'de') return badge.name_de;
    if (language === 'ar') return badge.name_ar;
    return badge.name_en;
  };

  const getBadgeDescription = (badge) => {
    if (language === 'de') return badge.description_de;
    if (language === 'ar') return badge.description_ar;
    return badge.description_en;
  };

  const isEarned = (badgeId) => earnedBadges.some(b => b.badge_id === badgeId);
  const getEarnedDate = (badgeId) => {
    const detail = earnedDetails.find(d => d.badge_id === badgeId);
    return detail?.earned_at ? new Date(detail.earned_at).toLocaleDateString() : null;
  };

  const earnedBadgeList = useMemo(() => allBadges.filter(b => isEarned(b.badge_id)), [allBadges, earnedBadges]);
  const lockedBadgeList = useMemo(() => allBadges.filter(b => !isEarned(b.badge_id)), [allBadges, earnedBadges]);
  const earnedCount = earnedBadges.length;
  const totalCount = allBadges.length;
  const progressPct = totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="achievements-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border hidden lg:block">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg"
              alt="EGNS Logo"
              className="h-10 w-auto"
            />
          </div>
          <nav className="space-y-2">
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors">
              <LayoutDashboard className="h-5 w-5" />
              {t('dashboard')}
            </Link>
            <Link to="/lessons" className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors">
              <BookOpen className="h-5 w-5" />
              {t('lessons')}
            </Link>
            <Link to="/achievements" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/10 text-primary font-medium">
              <Trophy className="h-5 w-5" />
              {t('achievements')}
            </Link>
            <Link to="/my-progress" className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors">
              <BarChart3 className="h-5 w-5" />
              My Progress
            </Link>
            <Link to="/profile" className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors">
              <User className="h-5 w-5" />
              {t('profile')}
            </Link>
            {(user?.email === 't.m@mimc.online' || user?.is_admin === true) && (
              <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-amber-500 hover:bg-amber-500/10 transition-colors">
                <Shield className="h-5 w-5" />
                Admin Panel
              </Link>
            )}
          </nav>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border">
          <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors" data-testid="logout-btn">
            <LogOut className="h-5 w-5" />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-20 lg:pb-0">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="lg:hidden">
                <Button variant="ghost" size="icon"><ChevronLeft className="h-5 w-5" /></Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">{t('achievements')}</h1>
                <p className="text-muted-foreground text-sm">
                  {earnedCount} of {totalCount} badges earned
                </p>
              </div>
            </div>
            <LanguageSelector />
          </div>
        </header>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={`skel-${i}`} className="h-40 rounded-xl" />
              ))}
            </div>
          ) : (
            <>
              {/* Progress Summary */}
              <Card className="border-t-4 border-t-amber-500" data-testid="badge-progress-card">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <Award className="h-7 w-7 text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold">Badge Collection</p>
                        <span className="text-sm font-bold text-amber-500">{progressPct}%</span>
                      </div>
                      <Progress value={progressPct} className="h-2.5" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {earnedCount}/{totalCount} badges unlocked
                        {user?.total_points ? ` | ${user.total_points.toLocaleString()} XP total` : ''}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Earned Badges */}
              {earnedCount > 0 && (
                <div>
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-500" /> Earned Badges
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {earnedBadgeList.map((badge) => (
                      <Card
                        key={badge.badge_id}
                        className="text-center border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-500/5 hover:shadow-lg transition-all"
                        data-testid={`badge-${badge.badge_id}`}
                      >
                        <CardContent className="pt-5 pb-4">
                          <span className="text-4xl block mb-2">{badge.icon}</span>
                          <h3 className="font-semibold text-sm mb-0.5">{getBadgeName(badge)}</h3>
                          <p className="text-[10px] text-muted-foreground leading-tight">{getBadgeDescription(badge)}</p>
                          <p className="text-[10px] text-amber-600 mt-1.5 font-medium">
                            {getEarnedDate(badge.badge_id) || 'Earned'}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Locked Badges */}
              {earnedCount < totalCount && (
                <div>
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Lock className="h-5 w-5 text-muted-foreground" /> Locked Badges
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {lockedBadgeList.map((badge) => (
                      <Card
                        key={badge.badge_id}
                        className="text-center opacity-60 hover:opacity-80 transition-all"
                        data-testid={`badge-${badge.badge_id}`}
                      >
                        <CardContent className="pt-5 pb-4">
                          <div className="relative inline-block mb-2">
                            <span className="text-4xl block grayscale">{badge.icon}</span>
                            <div className="absolute -bottom-1 -right-1 bg-slate-600 rounded-full p-1">
                              <Lock className="h-3 w-3 text-white" />
                            </div>
                          </div>
                          <h3 className="font-semibold text-sm mb-0.5">{getBadgeName(badge)}</h3>
                          <p className="text-[10px] text-muted-foreground leading-tight">{getBadgeDescription(badge)}</p>
                          <UiBadge variant="secondary" className="text-[10px] mt-1.5">
                            {badge.requirement_type === 'lessons_completed' && `${badge.requirement_value} lessons`}
                            {badge.requirement_type === 'points' && `${badge.requirement_value} XP`}
                            {badge.requirement_type === 'streak' && `${badge.requirement_value}-day streak`}
                            {badge.requirement_type === 'pronunciations' && `${badge.requirement_value} recordings`}
                            {badge.requirement_type === 'conversations' && `${badge.requirement_value} chats`}
                            {badge.requirement_type === 'quizzes_passed' && `${badge.requirement_value} quizzes`}
                            {badge.requirement_type === 'flashcards_reviewed' && `${badge.requirement_value} cards`}
                            {badge.requirement_type === 'pflege_completed' && 'Pflege module'}
                            {badge.requirement_type === 'level' && 'All A1 lessons'}
                          </UiBadge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Leaderboard */}
              {leaderboard.length > 0 && (
                <Card data-testid="leaderboard-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Crown className="h-5 w-5 text-amber-500" /> Weekly Leaderboard
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {leaderboard.slice(0, 10).map((entry, idx) => {
                      const isMe = entry.user_id === user?.user_id;
                      return (
                        <div
                          key={entry.user_id || `lb-${idx}`}
                          className={`flex items-center gap-3 p-2 rounded-lg ${
                            isMe ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/30'
                          }`}
                        >
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-amber-500 text-white' :
                            idx === 1 ? 'bg-slate-400 text-white' :
                            idx === 2 ? 'bg-amber-700 text-white' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isMe ? 'text-primary' : ''}`}>
                              {entry.name || entry.email?.split('@')[0]}
                              {isMe && <span className="text-xs ml-1">(you)</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 text-sm font-bold">
                            <Flame className="h-3.5 w-3.5 text-amber-500" />
                            {entry.total_points?.toLocaleString() || 0}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border lg:hidden">
        <div className="grid grid-cols-4 gap-1 p-2">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 py-2 text-muted-foreground">
            <LayoutDashboard className="h-5 w-5" />
            <span className="text-xs">{t('dashboard')}</span>
          </Link>
          <Link to="/lessons" className="flex flex-col items-center gap-1 py-2 text-muted-foreground">
            <BookOpen className="h-5 w-5" />
            <span className="text-xs">{t('lessons')}</span>
          </Link>
          <Link to="/achievements" className="flex flex-col items-center gap-1 py-2 text-primary">
            <Trophy className="h-5 w-5" />
            <span className="text-xs">{t('achievements')}</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 py-2 text-muted-foreground">
            <User className="h-5 w-5" />
            <span className="text-xs">{t('profile')}</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
